import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Star, ExternalLink, CalendarX2, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { AdminCard, PageTitle } from "@/pages/admin/AdminLayout";
import { TourEditor } from "@/pages/admin/TourEditor";
import { AvailabilityDialog } from "@/pages/admin/AvailabilityDialog";
import { centsToEuro, tourPricing, capacity } from "@/lib/pricing";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DEST_LABELS = { pompei: "Pompei", napoli: "Napoli", ercolano: "Ercolano", caserta: "Caserta", costiera: "Costiera Amalfitana", paestum: "Paestum", altro: "Altro" };
export const CAT_LABELS = { archaeology: "Archeologia", cities: "Città e cultura", food: "Cibo e vino", nature: "Natura e panorami", coast: "Costiera in auto" };
export const TYPE_LABELS = { group: "Di gruppo", small_group: "Piccolo gruppo", private: "Privato" };
export const STATUS_LABELS_TOUR = { published: "Pubblicato", draft: "Bozza", archived: "Archiviato" };
const STATUS_PILL = { published: "bg-emerald-50 text-emerald-800 border-emerald-200", draft: "bg-amber-50 text-amber-800 border-amber-200", archived: "bg-sand/60 text-ocean/60 border-sand" };

const durLabel = (t) => {
  const d = t.duration;
  if (!d) return `${t.duration_hours} ore`;
  const h = (m) => (m / 60).toLocaleString("it-IT", { maximumFractionDigits: 1 });
  return d.min_minutes === d.max_minutes ? `${h(d.max_minutes)} ore` : `${h(d.min_minutes)}–${h(d.max_minutes)} ore`;
};

const priceLabel = (t) => {
  const p = tourPricing(t);
  if (p.mode === "per_group") return `${centsToEuro(p.base_cents)} / gruppo (fino a ${p.base_participants})`;
  return `${centsToEuro(p.base_cents)} / persona`;
};

export default function AdminTours() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // tour object or "new"
  const [availFor, setAvailFor] = useState(null);
  const [view, setView] = useState("active"); // active | archived | all

  const load = () => adminApi.get("/admin/tours").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => list.filter((t) => (view === "all" ? true : view === "archived" ? t.status === "archived" : t.status !== "archived")), [list, view]);
  const counts = useMemo(() => ({ active: list.filter((t) => t.status !== "archived").length, archived: list.filter((t) => t.status === "archived").length }), [list]);

  const quick = async (tour, patch, msg = "Tour aggiornato") => {
    try {
      const { id, created_at, updated_at, closed_dates, archived_at, archived_reason, imported_by_migration, schema_v2_backfilled, catalogue_no, active, price, duration_hours, max_guests, ...rest } = tour;
      const r = await adminApi.put(`/admin/tours/${id}`, { ...rest, ...patch });
      setList((l) => l.map((x) => (x.id === id ? r.data : x)));
      toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (tour) => {
    if (!window.confirm(`Eliminare definitivamente "${tour.translations?.en?.title}"?\nConsigliato: archiviare invece di eliminare.`)) return;
    try {
      await adminApi.delete(`/admin/tours/${tour.id}`);
      setList((l) => l.filter((x) => x.id !== tour.id));
      toast.success("Tour eliminato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-tours">
      <PageTitle title="Tour" sub={`${counts.active} nel catalogo · ${counts.archived} archiviati. Prezzi, durate, capienze e inclusioni sono gestiti qui.`}
        right={<button onClick={() => setEditing("new")} className="inline-flex items-center gap-2 px-5 py-3 bg-ocean text-ivory text-sm font-medium rounded-xl btn-lift" data-testid="admin-tour-new"><Plus size={16} />Nuovo tour</button>} />
      <AdminCard className="p-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-xs tracking-[0.18em] uppercase text-ocean/55">Vista</span>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="sm:w-64 h-11 rounded-xl border-sand bg-white" data-testid="admin-tours-view"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="active">Catalogo (pubblicati e bozze)</SelectItem>
            <SelectItem value="archived">Archiviati</SelectItem>
            <SelectItem value="all">Tutti</SelectItem>
          </SelectContent>
        </Select>
      </AdminCard>
      <AdminCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-ivory/70 text-[11px] uppercase tracking-[0.15em] text-ocean/55">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Tour</th>
                <th className="text-left px-5 py-3 font-medium">Tipo</th>
                <th className="text-right px-5 py-3 font-medium">Prezzo</th>
                <th className="text-left px-5 py-3 font-medium">Stato</th>
                <th className="text-center px-5 py-3 font-medium">Home</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-sand/60">
              {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-ocean/45">Caricamento...</td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-ocean/45" data-testid="admin-tours-empty">Nessun tour in questa vista.</td></tr>}
              {shown.map((tour) => {
                const cap = capacity(tour);
                const archived = tour.status === "archived";
                return (
                  <tr key={tour.id} className={`hover:bg-sand/20 ${archived ? "opacity-70" : ""}`} data-testid={`admin-tour-row-${tour.slug}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={tour.image} alt="" className="w-14 h-10 rounded-lg object-cover bg-sand shrink-0" />
                        <div className="min-w-0">
                          <p className="text-ocean font-medium truncate max-w-[320px]">{tour.translations?.it?.title || tour.translations?.en?.title}</p>
                          <p className="text-xs text-ocean/45">
                            {DEST_LABELS[tour.destination] || "—"} · {durLabel(tour)} · {cap ? `max ${cap}` : "capienza n.d."}
                            {tour.meeting_point_status !== "confirmed" ? " · meeting point da confermare" : ""}
                            {tour.closed_dates?.length ? ` · ${tour.closed_dates.length} date chiuse` : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ocean/70 whitespace-nowrap">{TYPE_LABELS[tour.tour_type] || "—"}</td>
                    <td className="px-5 py-3 text-right font-medium whitespace-nowrap" data-testid={`admin-tour-price-${tour.slug}`}>{priceLabel(tour)}</td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_PILL[tour.status] || ""}`} data-testid={`admin-tour-status-${tour.slug}`}>{STATUS_LABELS_TOUR[tour.status] || tour.status}</span></td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => quick(tour, { featured: !tour.featured })} aria-label="In evidenza" disabled={archived} className="disabled:opacity-30 p-2" data-testid={`admin-tour-featured-${tour.slug}`}>
                        <Star size={18} className={tour.featured ? "text-sea fill-sea" : "text-ocean/25"} />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2 text-ocean/60">
                        <a href={`/tours/${tour.slug}`} target="_blank" rel="noreferrer" aria-label="Apri" className="p-2 hover:text-ocean"><ExternalLink size={16} /></a>
                        <button onClick={() => setAvailFor(tour)} aria-label="Disponibilità" title="Disponibilità e date chiuse" className="p-2 hover:text-ocean" data-testid={`admin-tour-availability-${tour.slug}`}><CalendarX2 size={16} /></button>
                        <button onClick={() => setEditing(tour)} aria-label="Modifica" className="p-2 hover:text-ocean" data-testid={`admin-tour-edit-${tour.slug}`}><Pencil size={16} /></button>
                        {archived
                          ? <button onClick={() => quick(tour, { status: "draft" }, "Tour ripristinato come bozza")} aria-label="Ripristina" title="Ripristina come bozza" className="p-2 hover:text-ocean" data-testid={`admin-tour-restore-${tour.slug}`}><ArchiveRestore size={16} /></button>
                          : <button onClick={() => quick(tour, { status: "archived", featured: false }, "Tour archiviato")} aria-label="Archivia" title="Archivia (nascondi dal sito)" className="p-2 hover:text-ocean" data-testid={`admin-tour-archive-${tour.slug}`}><Archive size={16} /></button>}
                        <button onClick={() => remove(tour)} aria-label="Elimina" className="p-2 text-rose-700" data-testid={`admin-tour-delete-${tour.slug}`}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AdminCard>
      <AvailabilityDialog tour={availFor} onClose={() => setAvailFor(null)} onSaved={(dates) => { setList((l) => l.map((x) => (x.id === availFor.id ? { ...x, closed_dates: dates } : x))); setAvailFor(null); }} />
      <TourEditor open={!!editing} tour={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} nextOrder={list.length} />
    </div>
  );
}
